import { Box, Button, InputAdornment, ListItemIcon, Menu, MenuItem, Stack, Typography, useMediaQuery } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import GeneratedReportService from '../../../../shared/services/generated-report/generatedReport.service';
import React, { useContext, useEffect, useState } from 'react';
import { IGeneratedSecrReportResponse } from '../../../../shared/services/generated-report/generated-report.model';
import { BackButtonLink } from '../../../presentation-components/display-elements-components/back-button-link';
import gaiaTheme from '../../../../root-components/theme';
import { ReportingSecrTable } from '../../../presentation-components/reporting-components/secr/reporting-secr-table';
import { AutoAwesomeOutlined, CheckCircleOutline, DownloadOutlined, EditOutlined, KeyboardArrowDownOutlined } from '@mui/icons-material';
import { DownloadCSV } from '../../../presentation-components/miscellaneous-components/download-csv';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import CreatePdfService from '../../../../shared/services/create-pdf/create-pdf.service';
import { LoadingButton } from '@mui/lab';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import AiCopilotService from '../../../../shared/services/ai-copilot/ai-copilot.service';
import { SecrDraftSectionType } from '../../../../shared/services/ai-copilot/ai-copilot.model';
import { CopilotPanel } from '../../../copilot/CopilotPanel';
import { FormTextInputDisplay } from '../../../presentation-components/form-field-components/new-and-edit/form-text-input';
import { toPlainCommentary } from '../../../../shared/utils/ai-text-utils';

type SecrNarrativeField = 'methodologyStatement' | 'energyEfficiencyNarrative';

const NARRATIVE_MAX = 5000;

// The two statutory SECR narratives, in the order the report prints them. Placeholders as on the form.
const SECR_NARRATIVES: {
  field: SecrNarrativeField;
  title: string;
  placeholder: string;
  section: SecrDraftSectionType;
  draftTitle: string;
  useDraftLabel: string;
}[] = [
  {
    field: 'methodologyStatement',
    title: 'Methodology statement',
    placeholder: 'e.g. GHG Protocol Corporate Standard; UK Government DEFRA conversion factors (gross CV); operational control boundary.',
    section: 'secr_methodology',
    draftTitle: 'Draft methodology statement',
    useDraftLabel: 'Use as methodology statement',
  },
  {
    field: 'energyEfficiencyNarrative',
    title: 'Energy efficiency action taken',
    placeholder: 'Describe the energy-efficiency measures taken during the reporting period.',
    section: 'secr_energy_efficiency',
    draftTitle: 'Draft energy efficiency narrative',
    useDraftLabel: 'Use as energy efficiency narrative',
  },
];

export const ReportingSecrTablePage = () => {

  const params = useParams();
  const navigate = useNavigate();
  const generatedReportService = new GeneratedReportService();
  const createPdfService = new CreatePdfService();
  const aiCopilotService = new AiCopilotService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const userDetailsContext = useContext(UserDetailsContext);
  const [report, setReport] = useState<IGeneratedSecrReportResponse>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [isSubmitting, setisSubmitting] = useState<boolean>(false)
  const [copilotSection, setCopilotSection] = useState<SecrDraftSectionType | null>(null)
  const [narrativeEditField, setNarrativeEditField] = useState<string>('')
  const [narrativeDraft, setNarrativeDraft] = useState<string | null>(null)
  const [savingNarrative, setSavingNarrative] = useState(false)
  const openDropdown = Boolean(anchorEl)
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const canUseCopilot = !!userDetailsContext.userDetails?.organisation?.aiCopilotEnabled;

  // The narratives are part of the report, so a completed report shows them read-only (or not at
  // all when blank). One field is in edit at a time; nothing is saved until the user saves.
  const isComplete = !!report?.markedAsCompleted;
  const editingNarrative = SECR_NARRATIVES.find((narrative) => narrative.field === narrativeEditField);
  const copilotNarrative = SECR_NARRATIVES.find((narrative) => narrative.section === copilotSection);
  const visibleNarratives = SECR_NARRATIVES.filter((narrative) => !isComplete || !!report?.[narrative.field]);
  const narrativeValue = (field: SecrNarrativeField): string => (
    narrativeEditField === field ? (narrativeDraft ?? report?.[field] ?? '') : (report?.[field] ?? '')
  );

  useEffect(() => {
    getReport();
  }, [])

  const getReport = () => {
    if (params.secrReportId) {
      generatedReportService.getReport(params.secrReportId)
        .then(response => {
          setReport(response);
        })
        .catch(err => console.log(err))
    }
  }

  const editHandler = () => {
    if (report) {
      navigate(`/reporting/secr/form?secrId=${report.id}`)
    }
  }

  const cancelNarrativeHandler = () => {
    setNarrativeEditField('');
    setNarrativeDraft(null);
  };

  const saveNarrativeHandler = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!report || !editingNarrative || savingNarrative) return;
    const value = narrativeValue(editingNarrative.field);
    if (value.length > NARRATIVE_MAX) return;
    setSavingNarrative(true);
    try {
      const text = value.trim() || null;
      const response = await generatedReportService.updateSecrNarratives(
        report.id,
        editingNarrative.field === 'methodologyStatement' ? { methodologyStatement: text } : { energyEfficiencyNarrative: text },
      );
      setReport((current) => (current ? {
        ...current,
        methodologyStatement: response.methodologyStatement,
        energyEfficiencyNarrative: response.energyEfficiencyNarrative,
      } : current));
      cancelNarrativeHandler();
      userNotificationsContext.openSnackbar({ open: true, message: 'Narrative saved' });
    } catch (err: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The narrative could not be saved, please try again',
        backgroundColor: 'text.primary',
      });
    } finally {
      setSavingNarrative(false);
    }
  };

  // "Use as ..." on a G.AI draft: the text goes into its field in edit state. Nothing is saved
  // until the user presses save.
  const applyDraftToNarrative = (text: string) => {
    if (!copilotNarrative) return;
    setNarrativeDraft(toPlainCommentary(text));
    setNarrativeEditField(copilotNarrative.field);
    setCopilotSection(null);
  };

  const completedHandler = () => {
    userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to record this SECR as complete for this report period? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        if (response && report && params.secrReportId) {
          if (!isSubmitting) {
            setisSubmitting(true)
            generatedReportService.completeSecrReport(report.id, params.secrReportId)
              .then(response => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
                cancelNarrativeHandler();
                setCopilotSection(null);
                getReport();
                setisSubmitting(false)
              })
              .catch(err => {
                console.log(err)
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                setisSubmitting(false)
              })
          }
        }
      })
  }


  const showPdfInBrowserHandler = () => {
    if (report) {
      createPdfService.getSecrReportPdf(`?generatedReportId=${report.id}`)
        .then(async (response) => {
          const blob = response instanceof Blob ? response : new Blob([response]);
          const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
          const looksLikePdfByMagic =
            head.length >= 5 &&
            head[0] === 0x25 && // %
            head[1] === 0x50 && // P
            head[2] === 0x44 && // D
            head[3] === 0x46 && // F
            head[4] === 0x2d;   // -
          const looksLikePdfByMime = (blob.type || '').toLowerCase().includes('pdf');
          const looksLikePdf = looksLikePdfByMagic || looksLikePdfByMime;
          if (!looksLikePdf) {
            userNotificationsContext.openSnackbar({ open: true, message: 'Could not generate PDF. Please try again.', backgroundColor: 'text.primary' });
            return;
          }
          const pdfBlob = blob.type ? blob : new Blob([blob], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(pdfBlob)
          const a = document.createElement('a');
          a.href = url;
          a.download = 'Streamlined Energy and Carbon Report.pdf';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        })
        .catch(err => {
          console.log(err)
          userNotificationsContext.openSnackbar({ open: true, message: 'Could not generate PDF. Please try again.', backgroundColor: 'text.primary' });
        })
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: 4, pb: isMobileModeSm ? 3 : 6, pl: isMobileModeSm ? 3 : 6, pr: isMobileModeSm ? 3 : 6, mt: isMobileModeSm ? 5 : 0, maxWidth: '100%' }}>
      <Stack display='flex' justifyContent='flex-start' sx={{ mb: 2 }}>
        <BackButtonLink buttonText={'Back to SECR reports'} link={'/reporting/secr'} />
        <Stack direction={isMobileModeMd ? 'column' : 'row'} justifyContent='space-between' alignItems={isMobileModeMd ? 'flex-start' : 'center'} sx={{ mb: report && report.markedAsCompleted ? 2 : 4 }}>
          {report && <Typography variant='h4' sx={{ color: 'text.primary', mb: isMobileModeMd ? 1 : 0 }}>Streamlined Energy and Carbon Report ({report.reportDetails[0].reportPeriodName}) </Typography>}
          <Stack flexDirection={'row'} sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <Stack display='flex' flexDirection='row' alignItems={'center'}>
              {report &&
                <>
                  {/* Secondary report actions consolidated into one dropdown; Complete Report stays the primary CTA. */}
                  <Box sx={{ position: 'relative', mr: 2 }}>
                    <Button
                      disabled={isSubmitting}
                      id="report-actions-button"
                      variant='outlined'
                      aria-controls={openDropdown ? 'report-actions-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openDropdown ? 'true' : undefined}
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      endIcon={<KeyboardArrowDownOutlined />}
                    >
                      <Typography>Actions</Typography>
                    </Button>
                    <Menu
                      id="report-actions-menu"
                      anchorEl={anchorEl}
                      open={openDropdown}
                      onClose={() => setAnchorEl(null)}
                      MenuListProps={{
                        'aria-labelledby': 'report-actions-button',
                      }}
                    >
                      <MenuItem
                        onClick={() => { setAnchorEl(null); editHandler(); }}
                        disabled={report.markedAsCompleted || isSubmitting}
                      >
                        <ListItemIcon><EditOutlined fontSize='small' /></ListItemIcon>
                        Edit Report
                      </MenuItem>
                      <DownloadCSV data={report} fileName='SECR Report' fileType='secr' />
                      <MenuItem onClick={() => { setAnchorEl(null); showPdfInBrowserHandler(); }}>
                        <ListItemIcon><DownloadOutlined fontSize='small' /></ListItemIcon>
                        PDF (.pdf)
                      </MenuItem>
                    </Menu>
                  </Box>
                  <LoadingButton
                    loading={isSubmitting}
                    loadingPosition="start"
                    startIcon={<CheckCircleOutline />}
                    onClick={completedHandler}
                    variant='contained'
                    disabled={report.markedAsCompleted}
                  >
                    <Typography>{isSubmitting ? 'Completing Report' : 'Complete Report'}</Typography>
                  </LoadingButton>
                </>
              }
            </Stack>
          </Stack >
        </Stack>
      </Stack >
      {report && visibleNarratives.length > 0 && (
        <Box component='form' onSubmit={saveNarrativeHandler} sx={{ mb: 4, maxWidth: '1100px' }}>
          <Typography variant='h6' sx={{ color: 'text.primary', mb: 1 }}>Narrative disclosures</Typography>
          {visibleNarratives.map((narrative, index) => {
            const isEditing = narrativeEditField === narrative.field;
            const value = narrativeValue(narrative.field);
            const tooLong = value.length > NARRATIVE_MAX;
            const canDraft = canUseCopilot && !isComplete && !savingNarrative && (narrativeEditField === '' || isEditing);
            return (
              <FormTextInputDisplay
                key={narrative.field}
                isEditPage={true}
                allowEdit={!isComplete && !savingNarrative}
                editField={narrativeEditField}
                setEditField={setNarrativeEditField}
                disabled={!isEditing || savingNarrative}
                title={narrative.title}
                name={narrative.field}
                value={value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNarrativeDraft(e.target.value)}
                cancelEditHandler={cancelNarrativeHandler}
                multiline={true}
                rows={4}
                isTopRow={index === 0}
                isBottomRow={index === visibleNarratives.length - 1}
                placeholder={narrative.placeholder}
                customErrorMessage={tooLong ? `The narrative can be at most ${NARRATIVE_MAX.toLocaleString('en-GB')} characters.` : undefined}
                helperText={isComplete
                  ? 'This report is complete, so its narrative can no longer change.'
                  : isEditing && value.length > 4500
                    ? `${value.length.toLocaleString('en-GB')} of ${NARRATIVE_MAX.toLocaleString('en-GB')} characters`
                    : 'Printed in this report and its PDF.'}
                endAdornment={canDraft ? (
                  <InputAdornment position='end'>
                    <Button
                      variant='text'
                      size='small'
                      startIcon={<AutoAwesomeOutlined fontSize='small' />}
                      onClick={() => setCopilotSection(narrative.section)}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Draft with G.AI
                    </Button>
                  </InputAdornment>
                ) : undefined}
              />
            );
          })}
        </Box>
      )}
      {report && <ReportingSecrTable report={report} hideNarratives={true} />}
      {canUseCopilot && report && copilotNarrative && (
        <CopilotPanel
          key={copilotNarrative.section}
          open={!!copilotNarrative}
          title={copilotNarrative.draftTitle}
          onClose={() => setCopilotSection(null)}
          request={() => aiCopilotService.draftSecrSection({
            generatedReportId: report.id,
            sectionType: copilotNarrative.section,
          })}
          onUseDraft={isComplete ? undefined : applyDraftToNarrative}
          useDraftLabel={copilotNarrative.useDraftLabel}
        />
      )}
    </Box >
  )
}